<script lang="ts" setup>
  import { useI18n } from 'vue-i18n'
  import { useQuery } from '@tanstack/vue-query'

  import { REGIONS } from '~/constants/REGIONS'
  import { useRegionsService } from '~/core/services'
  import { BngContentFit, BngPageFooter } from '~/layouts/components'

  const config = useRuntimeConfig()
  const { nearbyGolfs } = useNearbyGolfs()
  const { t } = useI18n()
  const observable = useTemplateRef('observable')
  const { getRegions } = useRegionsService()

  // Query

  const { data: regions, suspense } = useQuery({
    enabled: true,
    queryKey: ['home', 'regions'],
    queryFn: () => getRegions(),
    staleTime: 10 * 1000,
  })

  const displayedRegions = computed(() => REGIONS
    .map((mr) => ({ ...mr, ...regions.value?.data?.find((r) => mr.code === r.code) }))
    .sort((a, b) => (b.golfs_count ?? 0) - (a.golfs_count ?? 0))
    ?? [])

  function handleSearchHeightChange() {
  }

  useHead({
    title: 'Bookandgolf | Réserver au meilleur tarif',
    meta: [
      {
        name: 'description',
        content: 'Réservez votre parcours en ligne en quelques clics partout en France !',
      },
    ],
  })

  onServerPrefetch(async () => {
    await suspense()
  })

</script>
<template>
  <div class="p-index">
    <ScrollReactiveAlt
      v-slot="{collapsed}"
      :observed-element="observable"
      :threshold="0.5"
      @change="handleSearchHeightChange"
    >
      <HomeSearch :collapsed="collapsed">
        <template #header>
          <div class="search-header">
            <SvgIcon
              class="large-icon"
              :height="38"
              :width="38"
              icon="arrow"
            />
            <div class="title">
              <span>{{ t("search.title.base") }}, </span>
              <span class="colored">{{ t("search.title.simple") }} </span>
              <span>{{ t("search.title.and") }} </span>
              <span class="colored">{{ t("search.title.fast") }}&nbsp;</span>
              <span>!</span>
            </div>
          </div>
        </template>
      </HomeSearch>
    </ScrollReactiveAlt>
    <div
      ref="observable"
      class="top-page-background"
    >
      <div
        class="illustration-wrapper"
      >
        <div
          v-for="item in ['parcours', 'annulation', 'promotion', 'golf-player']"
          :key="`illustration-${item}`"
          class="top-page-illustration"
        >
          <SvgIcon
            class="illustration"
            :alt="item"
            :height="80"
            :width="80"
            :desktop-height="100"
            :desktop-width="100"
            :icon="`illustration-${item}`"
          />
          <div class="subtitle">
            <span>{{ t(`home.${item}`) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="nearbyGolfs?.data.length"
      class="nearby-golfs"
    >
      <BngContentFit>
        <div class="nearby-golfs-title">
          <SvgIcon
            :height="20"
            :width="20"
            :desktop-height="30"
            :desktop-width="30"
            icon="sight"
          />
          <p class="title">
            <span>{{ t("home.nearby-golfs") }}</span>
          </p>
        </div>
        <div class="scroll-container">
          <ScrollH class="nearby-golfs-cards">
            <Paper
              v-for="item of nearbyGolfs?.data ?? []"
              :key="item.id"
              class="hover-shadow"
            >
              <GolfCardWrapper
                :golf="item"
                is-home
                rating
              />
            </Paper>
          </ScrollH>
        </div>
      </BngContentFit>
    </div>
    <div
      v-if="displayedRegions.length"
      class="regions"
    >
      <BngContentFit>
        <div class="regions-title">
          <SvgIcon
            :height="26"
            :width="26"
            :desktop-height="28"
            :desktop-width="28"
            icon="france"
            style="color: var(--color-primary)"
          />
          <p class="title">
            <span>{{ t("home.region-golfs") }}</span>
          </p>
        </div>
        <div class="regions-cards">
          <HomeRegionsSlider :items="displayedRegions">
            <template #item="{ item }">
              <Paper
                :key="item.slug"
                class="hover-shadow"
              >
                <RegionCard :region="item" />
              </Paper>
            </template>
          </HomeRegionsSlider>
        </div>
        <div
          class="scroll-container"
        >
          <ScrollH class="scroll-regions-cards">
            <Paper
              v-for="region of displayedRegions"
              :key="region.slug"
              class="hover-shadow"
            >
              <RegionCard :region="region" />
            </Paper>
          </ScrollH>
        </div>
      </BngContentFit>
    </div>
    <HomeProSection v-if="!config.public.isMobile" />
    <BngPageFooter />
  </div>
</template>
<style scoped>
.p-index {
  position: relative;
  display: grid;
  flex-direction: column;
  background-color: #eef0f1;

  --displayed-nearby-golfs: 1;
  --displayed-regions: 1;

  .c-scroll-reactive {
    position: sticky;
    z-index: 2;
    top:0;
    height: min-content;
    grid-column: 1;
    grid-row: 1;
    transition: transform 0.5s ease;

    &.collapsed{
      transform: translateY(calc(-100% + 3.5em * 1.5));
    }
  }

  .top-page-background {
    position: relative;
    z-index: 1;
    bottom: 0;
    display: flex;
    height: 50em;
    flex-flow: row wrap;
    background: url("~/assets/illustrations/illus-green.svg") center center;
    background-size: cover;
    grid-column: 1;
    grid-row: 1;

    .illustration-wrapper {
      position: absolute;
      right: 1.5em;
      bottom: 80px;
      left: 1.5em;
      display: flex;
      flex-flow: row wrap;
      justify-content: space-evenly;
      margin-top: 1.5em;
      transition: ease top 0.5s;

      .top-page-illustration {
        bottom: 0;
        display: flex;
        width: 6em;
        height: 5em;
        flex-direction: column;
        align-items: center;
        justify-content: space-between;
        margin: 1rem 1rem 3rem;

        .illustration {
          color: var(--color-secondary);
        }

        .subtitle {
          height: 2em;
          text-align: center;

          span {
            color: var(--color-secondary);
            font-family: Open-Sans, sans-serif;
            font-size: 1em;
            font-weight: 600;
          }
        }
      }
    }
  }

  .nearby-golfs {
    position: relative;
    z-index: 0;
    display: flex;
    overflow: hidden;
    flex-direction: row;
    align-items: center;
    padding-top: 3em;

    .lc-content-fit {
      padding: 0;
    }

    .nearby-golfs-title {
      display: flex;
      flex-direction: row;
      padding-left: 1.5em;

      .c-icon {
        margin-right: 0.75em;
      }

      .title {
        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 2em;
          font-weight: 700;
        }
      }
    }

    .scroll-container {
      position: relative;

      .nearby-golfs-cards {
        position: relative;
        z-index: 0;
        display: flex;
        max-width: calc(100vw - 3em);
        box-sizing: content-box;
        flex-direction: row;
        padding: 1.5em;
        background-color: var(--color-secondary-light);
        grid-gap: 1.5em;

        .c-paper {
          overflow: hidden;
          max-width: 320px;
          flex: 0 0 calc((100% / var(--displayed-nearby-golfs)) - var(--gap));
          margin: 0;

          --gap: 1.5em * (var(--displayed-nearby-golfs) - 1) / var(--displayed-nearby-golfs);
        }
      }
    }
  }

  .regions {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding-top: 3em;
    margin-bottom: 3em;

    .lc-content-fit {
      padding: 0;
    }

    .regions-title {
      display: flex;
      flex-direction: row;
      padding-left: 1.5em;

      .c-icon {
        margin-right: 0.5em;
      }

      .title {
        span {
          color: var(--color-secondary);
          font-family: Bariol, sans-serif;
          font-size: 1.5em;
          font-weight: 700;
        }
      }
    }

    .regions-cards {
      display: none;
    }

    .scroll-container {
      position: relative;
      display: flex;

      .scroll-regions-cards {
        position: relative;
        z-index: 0;
        display: flex;
        max-width: calc(100vw - 3em);
        box-sizing: content-box;
        flex-direction: row;
        padding: 1.5em;
        background-color: var(--color-secondary-light);
        grid-gap: 1.5em;

        .c-paper {
          overflow: hidden;
          max-width: 320px;
          flex: 0 0 calc((100% / var(--displayed-regions)) - var(--gap) - 1.5em);
          margin: 0;

          --gap: 1.5em * (var(--displayed-regions) - 1) / var(--displayed-regions);
        }
      }
    }
  }

  @media screen and (width >= 600px) {
    --displayed-nearby-golfs: 2;
    --displayed-regions: 2;
  }

  @media screen and (width >= 1024px) {
    --displayed-nearby-golfs: 3;

    .c-scroll-reactive {
      &.collapsed{
        transform: translateY(calc(-100% + 7em * 1.5));
      }
    }

    .search-header {
      display: flex;
      flex-direction: row;
      align-items: center;

      .c-icon {
        margin: 0 1.25rem 0 0;
        color: var(--color-primary);
      }

      .title {
        color: var(--color-secondary);
        font-family: Bariol, sans-serif;
        font-size: 2rem;
        font-weight: 700;

        .colored {
          color: var(--color-primary);
        }
      }
    }

    .top-page-background {
      z-index: 0;
      display: flex;
      height: 45em;
      flex-wrap: unset;
      background-image: url("~/assets/illustrations/search-background-small.jpg");
      background-repeat: no-repeat;

      &::after {
        position: absolute;
        z-index: 0;
        background-image: linear-gradient(to bottom, rgb(0 0 0 / 0%), rgb(0 0 0 / 25%));
        content: "";
        inset: 0;
      }

      .illustration-wrapper {
        z-index: 1;
        bottom: 180px;
        display: flex;
        max-width: 1400px;
        flex: 1 1 auto;
        justify-content: space-between;
        padding: 0 2em;
        margin: 0 auto;

        .top-page-illustration {
          flex: 1;

          .illustration {
            height: 8rem;
            color: white;
          }

          .subtitle {
            span {
              color: white;
            }
          }
        }
      }
    }

    .nearby-golfs {
      .lc-content-fit {
        padding: 0 2.5em;
      }

      .nearby-golfs-title {
        padding-left: 4em;

        .c-icon {
          margin-right: 0.75em;
        }

        .title {
          span {
            font-size: 2em;
          }
        }
      }

      .scroll-container {
        margin: 0 2.5em;

        .nearby-golfs-cards {
          max-width: calc(100vw - 8.5em);
        }

        &::after,
        &::before {
          position: absolute;
          z-index: 1;
          bottom: 0;
          width: 1.5em;
          height: 100%;
          content: "";
        }

        &::after {
          right: 0;
          background: linear-gradient(to left, var(--color-secondary-light), transparent);
        }

        &::before {
          left: 0;
          background: linear-gradient(to right, var(--color-secondary-light), transparent);
        }
      }
    }

    .regions {
      overflow: hidden;

      .lc-content-fit {
        padding: 0 2.5em;
      }

      .regions-title {
        padding-left: 4em;

        .c-icon {
          margin-right: 0.75em;
        }

        .title {
          span {
            font-size: 2em;
          }
        }
      }

      .regions-cards {
        display: flex;
        max-width: calc(100vw - 8.5em);
        box-sizing: content-box;
        flex-direction: row;
        padding: 1.5em;
        margin: 0 2.5em;

        .c-paper {
          margin: 0;
        }
      }

      .scroll-container {
        display: none;
      }
    }
  }

  @media screen and (width >= 1280px) {
    --displayed-nearby-golfs: 4;
  }

  @media screen and (width >= 1600px) {
    --displayed-nearby-golfs: 5;
  }
}
  </style>
